import { useCallback, useMemo, memo } from 'react';
import styled from 'styled-components';
// import { useNavigate } from 'react-router';

import { useDownloadAppModal } from '@hooks/useDownloadAppModal';
import { formatNumber } from '@utils/index';
import { PremierText } from '@components/text';
import TouchableOpacity from '@components/buttons/TouchableOpacity';
import { useUserDataById } from '@redux/selectors/users';

const Container = styled.div`
  display: flex;
  flex-direction: row;
  padding-inline: 16px;
  padding-top: 4px;
  overflow-x: auto;
  z-index: 1;
  ${({ theme }) => theme.scrollbar.horizontal}
`;

const ItemContainer = styled(TouchableOpacity)`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  margin-block: 4px;
  margin-inline: 2px;
  padding: 12px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) =>
      `${theme.colors.white}${theme.opacity[10]}`};
    transform: translateY(-2px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const Amount = styled(PremierText)`
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fonts.fontSize['2xl']};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.fonts.fontSize.lg};
  }
`;

const Label = styled(PremierText)`
  flex-wrap: wrap;
  text-align: center;
  padding-inline: 4px;
  font-size: ${({ theme }) => theme.fonts.fontSize.lg};
  color: ${({ theme }) => theme.colors.grey[300]};
  font-weight: ${({ theme }) => theme.fonts.fontWeight.medium};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.fonts.fontSize.base};
  }
`;

type Props = {
  userId: string;
};

const LABELS: { [key: string]: string } = {
  followers: 'Followers',
  totalLikes: 'Total Likes',
  showsVisited: 'Guest Apps',
  showsHosted: 'Shows Hosted',
  debatesWon: 'Debates Won',
};

const Statistics = ({ userId }: Props) => {
  const userData = useUserDataById(userId);
  //   const navigate = useNavigate();
  const data = useMemo(
    () => ({
      followers: userData?.followers || 0,
      totalLikes: userData?.totalLikes || 0,
      showsVisited: userData?.showsVisited || 0,
      showsHosted: userData?.showsHosted || 0,
      debatesWon: Math.round(userData?.debatesWon || 0),
    }),
    [
      userData?.followers,
      userData?.totalLikes,
      userData?.showsHosted,
      userData?.showsVisited,
      userData?.debatesWon,
    ],
  );
  const items: [string, number][] = Object.entries(data);
  const { openDownloadAppModal } = useDownloadAppModal();

  const onClick = useCallback(
    (field: string) => {
      switch (field) {
        case 'followers':
          // TODO: navigate('UserFollowers');
          openDownloadAppModal();
          break;
        default:
          break;
      }
    },
    [openDownloadAppModal],
  );

  return (
    <Container>
      {items.map(([key, value]) => (
        <ItemContainer
          key={key}
          disabled={key !== 'followers'}
          onClick={() => onClick(key)}
        >
          <Amount>
            {LABELS[key] === LABELS.debatesWon
              ? `${value}%`
              : formatNumber(value)}
          </Amount>

          <Label>{LABELS[key]}</Label>
        </ItemContainer>
      ))}
    </Container>
  );
};

export default memo(Statistics);
